import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Box, Button, CircularProgress, GlobalStyles, Stack, Typography } from '@mui/material';
import { organizationApi } from '../../api';
import { getCurrentOrgId } from '../../api/client';
import { PRODUCT, type ProductCode } from '../../branding';
import { STORE_APPS, appBrandsFor, appPageUrl } from '../../components/face/appStore';

/**
 * A printable A4 sheet that gets a site's team onto the mobile app.
 *
 * Why it exists: the workforce is deskless and there is no time clock, so what an admin
 * needs at a site is something to put on the wall by the door — a QR code and three steps
 * (docs/plans/APP_QR_LAUNCH_PLAN_2026-07-30.md step 4). Opened from the "Get the Mobile
 * App" dialog; rendered OUTSIDE the admin shell so the printout carries no navigation.
 *
 * One app per sheet. A bundle organization's people install QuTime, which carries PureOA
 * too; a PureOA-only organization gets the PureOA sheet. The QR encodes svdy.com/app, which
 * opens the right store on iPhone and Android. An app with no public listing on either store
 * gets no code; with one store live, the sheet says which store has it.
 *
 * Language: the reader's UI language. An admin printing for a team that reads another
 * language switches language first; the sheet follows.
 */
const AppPosterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState<string | null>(null);
  const [brand, setBrand] = useState<ProductCode | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let name = '';
      let products: string[] | undefined;
      try {
        const orgs = await organizationApi.listMyOrganizations();
        const current = orgs.find((o) => o.id === getCurrentOrgId()) ?? orgs[0];
        name = current?.name ?? '';
        products = current?.licensed_products;
      } catch {
        // The sheet still works without the name; the brand falls back to the build.
      }
      if (cancelled) return;
      const brands = appBrandsFor(products, PRODUCT);
      setOrgName(name);
      setBrand(brands.includes('qutime') ? 'qutime' : brands[0]);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!brand) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const app = STORE_APPS[brand];

  return (
    <Box sx={{ bgcolor: { xs: '#fff', md: 'grey.100' }, minHeight: '100vh', py: { xs: 0, md: 3 } }}>
      <GlobalStyles styles={{
        '@page': { size: 'A4 portrait', margin: 0 },
        '@media print': {
          body: { background: '#fff' },
          '.no-print': { display: 'none !important' },
          '.poster-sheet': { boxShadow: 'none !important', border: 'none !important', margin: '0 !important' },
        },
      }} />

      <Stack direction="row" spacing={1} justifyContent="center" className="no-print" sx={{ mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          // The dialog opens this sheet in a NEW tab, where there is no history to go back to.
          // A tab a script opened can close itself; one opened by hand returns to the admin.
          onClick={() => { window.close(); window.setTimeout(() => navigate('/dashboard'), 150); }}
          sx={{ textTransform: 'none' }}
        >
          {t('common:actions.close')}
        </Button>
        {app.live && (
          <Button size="small" variant="contained" disableElevation onClick={() => window.print()} sx={{ textTransform: 'none' }}>
            {t('common:appPoster.print')}
          </Button>
        )}
      </Stack>

      <Box
        className="poster-sheet"
        sx={{
          width: '210mm',
          minHeight: '297mm',
          mx: 'auto',
          bgcolor: '#fff',
          border: '1px solid',
          borderColor: 'divider',
          boxSizing: 'border-box',
          px: '20mm',
          py: '22mm',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          color: '#1d1d1f',
        }}
      >
        {orgName && (
          <Typography sx={{ fontSize: '14pt', fontWeight: 600, color: '#6e6e73', mb: '6mm' }}>{orgName}</Typography>
        )}
        <Typography component="h1" sx={{ fontSize: '30pt', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.02em', mb: '12mm' }}>
          {t('common:appPoster.title', { product: app.name })}
        </Typography>

        {app.live ? (
          <>
            <Box sx={{ p: '4mm', border: '1px solid #d2d2d7', borderRadius: '4mm', lineHeight: 0, mb: '4mm' }}>
              <QRCodeSVG value={appPageUrl(brand)} size={340} level="M" includeMargin={false} style={{ width: '85mm', height: '85mm' }} />
            </Box>
            <Typography sx={{ fontSize: '11pt', color: '#6e6e73', mb: '14mm' }}>
              {t(app.appStoreLive && app.googlePlayLive
                ? 'common:appPoster.stores'
                : app.googlePlayLive ? 'common:appPoster.storesGooglePlayOnly' : 'common:appPoster.storesAppStoreOnly')}
            </Typography>

            <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, width: '100%', textAlign: 'start' }}>
              {[
                t('common:appPoster.step1', { product: app.name }),
                t('common:appPoster.step2'),
                t(brand === 'qutime' ? 'common:appPoster.step3Qutime' : 'common:appPoster.step3Pureoa'),
              ].map((text, i) => (
                <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'center', gap: '6mm', mb: '7mm' }}>
                  <Box sx={{
                    flexShrink: 0, width: '12mm', height: '12mm', borderRadius: '50%', bgcolor: '#1d1d1f', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16pt', fontWeight: 700,
                  }}>
                    {i + 1}
                  </Box>
                  <Typography sx={{ fontSize: '16pt', lineHeight: 1.35 }}>{text}</Typography>
                </Box>
              ))}
            </Box>

            <Typography sx={{ mt: 'auto', fontSize: '10pt', color: '#86868b' }}>
              {t('common:appPoster.footer', { url: 'svdy.com/app' })}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: '14pt', color: '#6e6e73' }}>{t('worker:getApp.comingSoon')}</Typography>
        )}
      </Box>
    </Box>
  );
};

export default AppPosterPage;
